import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { updateLoanDocuments } from '../../api/loanApplication';
import { useDecodedToken } from '../../utils/useDecodedToken';
import {
  collectDocumentUploadItems,
  emptyLoanAttachments,
  LOAN_DOCUMENT_SLOTS,
  type LoanAttachmentsState,
} from '../../constants/loanDocumentTypes';

interface LoanDocumentUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
  loanId: number;
  loanNumber?: string;
}

const LoanDocumentUploadModal: React.FC<LoanDocumentUploadModalProps> = ({
  open,
  onClose,
  onSubmitSuccess,
  loanId,
  loanNumber
}) => {
  // Validate loanId on mount and when it changes
  React.useEffect(() => {
    if (open && (isNaN(loanId) || loanId <= 0)) {
      console.error('[LoanDocumentUploadModal] Invalid loanId:', loanId);
      setError('Invalid loan ID. Please refresh the page and try again.');
    }
  }, [open, loanId]);
  const user = useDecodedToken();
  const [attachments, setAttachments] = useState<LoanAttachmentsState>(emptyLoanAttachments());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const removeSingleFile = (key: keyof LoanAttachmentsState) => {
    setAttachments(prev => ({ ...prev, [key]: null }));
  };

  const removeMultiFile = (key: keyof LoanAttachmentsState, index: number) => {
    setAttachments(prev => {
      const currentFiles = (prev[key] as File[]) || [];
      const newFiles = currentFiles.filter((_, i) => i !== index);
      return { ...prev, [key]: newFiles };
    });
  };

  const clearAllFiles = (key: keyof LoanAttachmentsState) => {
    setAttachments(prev => ({ ...prev, [key]: [] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate loanId first
    if (isNaN(loanId) || loanId <= 0 || !isFinite(loanId)) {
      setError('Invalid loan ID. Please close this dialog and try updating the loan again.');
      return;
    }
    
    const files = collectDocumentUploadItems(attachments);
    
    if (files.length === 0) {
      setError('Please upload at least one document.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Double-check loanId is valid before calling API
      const validLoanId = Number(loanId);
      if (isNaN(validLoanId) || validLoanId <= 0 || !isFinite(validLoanId)) {
        setError('Invalid loan ID. Please refresh the page and try again.');
        setLoading(false);
        return;
      }
      console.log('[LoanDocumentUploadModal] Uploading documents for loan ID:', validLoanId, 'Type:', typeof validLoanId);
      // Use the dedicated document update endpoint (no validation for required documents)
      await updateLoanDocuments(validLoanId, files, 3);
      setSuccess(true);
      setTimeout(() => {
        onSubmitSuccess();
        onClose();
        // Reset form
        setAttachments(emptyLoanAttachments());
        setSuccess(false);
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
    // Reset form when closing
    setAttachments(emptyLoanAttachments());
    setError(null);
    setSuccess(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Upload Required Documents
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Documents uploaded successfully! The loan management system has been notified.
          </Alert>
        ) : (
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Your loan application requires additional documents. Please upload the requested documents below. 
                All documents will be saved to the same location as your initial loan application.
              </Typography>
              {loanNumber && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Loan Number: {loanNumber}
                </Typography>
              )}
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Single-file uploads */}
                {LOAN_DOCUMENT_SLOTS.filter((item) => !item.multiple).map((item) => (
                  <Grid item xs={12} md={6} key={item.key}>
                    <Box sx={{ p: 2, border: '1px dashed', borderColor: 'grey.300', borderRadius: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>{item.label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Button variant="outlined" component="label" sx={{ textTransform: 'none' }} disabled={loading}>
                          Choose file
                          <input 
                            hidden 
                            type="file" 
                            onChange={e => setAttachments(p => ({ ...p, [item.key]: e.target.files?.[0] || null }))} 
                          />
                        </Button>
                        {(() => {
                          const f: any = (attachments as any)[item.key];
                          return f ? (
                            <Chip
                              label={f.name || '1 file selected'}
                              onDelete={() => removeSingleFile(item.key as keyof LoanAttachmentsState)}
                              deleteIcon={<DeleteIcon />}
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">No file chosen</Typography>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Grid>
                ))}

                {/* Multi-file uploads */}
                {LOAN_DOCUMENT_SLOTS.filter((item) => item.multiple).map((item) => (
                  <Grid item xs={12} key={item.key}>
                    <Box sx={{ p: 2, border: '1px dashed', borderColor: 'grey.300', borderRadius: 2, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">{item.label} (multiple)</Typography>
                        {Array.isArray((attachments as any)[item.key]) && (attachments as any)[item.key].length > 0 && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => clearAllFiles(item.key as keyof LoanAttachmentsState)}
                            sx={{ textTransform: 'none' }}
                            disabled={loading}
                          >
                            Clear All
                          </Button>
                        )}
                      </Box>
                      <Button variant="outlined" component="label" sx={{ textTransform: 'none', mb: 1 }} disabled={loading}>
                        Choose files
                        <input 
                          hidden 
                          multiple 
                          type="file" 
                          onChange={e => setAttachments(p => ({ ...p, [item.key]: e.target.files ? Array.from(e.target.files) : [] }))} 
                        />
                      </Button>
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Array.isArray((attachments as any)[item.key]) && (attachments as any)[item.key].length > 0 ? (
                          (attachments as any)[item.key].map((f: File, idx: number) => (
                            <Chip
                              key={idx}
                              label={f.name}
                              onDelete={() => removeMultiFile(item.key as keyof LoanAttachmentsState, idx)}
                              deleteIcon={<DeleteIcon />}
                              color="primary"
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">No files chosen</Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </form>
          </Box>
        )}
      </DialogContent>
      {!success && (
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleClose} disabled={loading} color="error" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading} sx={{ minWidth: 120 }}>
            {loading ? <CircularProgress size={24} /> : 'Upload Documents'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default LoanDocumentUploadModal;
